import type { RoleId } from '@onuw/roles-data'
import type { RoleGuide } from './types'

export const DAYBREAK_GUIDES = {
  sentinel: {
    lead: 'Places a shield on another player’s card: nobody may look at it or move it for the rest of the night.',
    does: [
      'The Sentinel wakes before the Werewolves and may put the Shield token on any other player’s card. The shield lies in plain sight for the rest of the game.',
    ],
    rules: [
      'She cannot shield her own card.',
      'A shielded card cannot be viewed, robbed, swapped or revealed by any later role, and the [[villageIdiot]]’s shift goes around it.',
      'The shield covers the card, not what lies beside it: a mark can still be given, traded or looked at, and the [[curator]] can still place an artifact there.',
      'A shielded [[insomniac]] cannot look at her own card.',
    ],
    tips: [
      'The shielded player is the one person at the table who certainly still holds the card they were dealt. Shield someone whose claim you want to be able to trust.',
      'Shielding a suspected wolf works too: nobody can take the wolf card away from them.',
    ],
    against: [
      'A shielded player cannot say their card was swapped. Whatever they were dealt, they still hold, which wolves dislike.',
    ],
  },
  alphaWolf: {
    lead: 'A Werewolf who makes another one: he swaps the extra Center Werewolf card into a player’s seat.',
    does: [
      'The Alpha Wolf wakes with the pack. Then he wakes again and must swap the Center Werewolf (a fourth center card, placed sideways when the Alpha Wolf is in the deck) with the card of a player who is not a werewolf.',
    ],
    rules: [
      'The deck needs a [[werewolf]] card as well, and holds four cards more than there are players instead of three.',
      'The swap is not optional. He cannot choose himself, another wolf or a shielded player.',
      'The new wolf is not told. Unless a later role shows them their card, they play as what they were dealt.',
      'The player’s old card takes the Center Werewolf’s place in the center.',
    ],
    tips: [
      'There is now one more werewolf at the table, and the village wins by killing any of you, the new one included. Protect them without showing why.',
      'Give the card to a player whose honest story will be believed: their truth now covers a werewolf.',
    ],
    against: [
      'With the Alpha Wolf in the deck, any villager may be a wolf without knowing it. The [[insomniac]] and the [[revealer]] can find out; a [[sentinel]]’s shield prevents it.',
    ],
  },
  mysticWolf: {
    lead: 'A Werewolf with a Seer’s eye: after the pack wakes, he looks at one player’s card.',
    does: [
      'The Mystic Wolf wakes with the Werewolves, then wakes again alone and may look at one other player’s card.',
    ],
    rules: [
      'He is a werewolf in every way: seen by the pack, by the [[minion]] and by the [[squire]].',
      'He looks before the [[robber]] and the [[troublemaker]] move cards, as the [[seer]] does, and cannot look at a shielded card.',
    ],
    tips: [
      'A wolf who knows a real card can claim Seer and back it with a true fact.',
      'Look at the player most likely to be the Seer: you learn whose claim you will have to beat.',
    ],
    against: [
      'A Seer claim backed by one true fact is not proof. The Mystic Wolf has exactly that. Ask what else they know.',
    ],
  },
  appSeer: {
    lead: 'Looks at one card in the center.',
    does: ['The Apprentice Seer may look at one of the center cards.'],
    rules: [
      'She counts as a seer: the [[beholder]] sees who holds her card at the end of the night.',
      'The [[witch]] and the [[drunk]] wake later and may take the card she saw out of the center.',
    ],
    tips: [
      'One center card is one role nobody was dealt. Anyone who claims they were dealt it is lying, unless the deck holds two.',
      'Tell the Drunk if they took the card you looked at: you can tell them what they are.',
    ],
    against: ['Her knowledge is small and hard. A wolf who claims the role she saw in the center is caught.'],
  },
  paranormalInvestigator: {
    lead: 'Looks at up to two players’ cards, and becomes the first one he finds that is not on the village’s side.',
    does: [
      'The Paranormal Investigator may look at one player’s card, and then at a second. If a card he sees is not a village card, he stops looking and becomes that role: a Werewolf, a Tanner, a Vampire, an Alien.',
    ],
    rules: [
      'Any card whose team is not the village’s turns him, the [[minion]] and the [[squire]] included. A [[doppelganger]] or a [[copycat]] card does not.',
      'The change belongs to his card and travels with it if the card is moved later.',
      'A P.I. who became a Werewolf did not wake with the pack: the wolves do not know him, and he knows only the one he looked at.',
      'He may stop after one card, or not look at all.',
    ],
    tips: [
      'Every look is a gamble. One look that finds a villager is solid information; a second look doubles the risk of changing sides.',
      'If you turned, you know one of your new team mates and they do not know you. Help them without being asked.',
    ],
    against: [
      'A P.I. who says he saw two villagers is telling the truth or covering for the wolf he found. Ask the two players he names what they were dealt.',
    ],
  },
  witch: {
    lead: 'Looks at a center card and, having looked, must swap it with a player’s card.',
    does: [
      'The Witch may look at one center card. If she does, she must exchange it with the card of any player, herself included.',
    ],
    rules: [
      'She knows which card she handed out; the player who received it does not.',
      'The card she took from that player goes to the center unseen.',
      'She wakes after the [[robber]] and before the [[troublemaker]], and cannot choose a shielded player.',
    ],
    tips: [
      'Found a village role? Give it to the player you suspect. If they were a wolf, the wolf card now lies in the center and they are harmless.',
      'Found a Werewolf? You have to put it in front of someone. Put it in front of another player and say so: you have made a wolf whom you can name.',
      'Taking the card yourself tells you exactly what you are, which few players can say.',
    ],
    against: [
      'A Witch claim rewrites two stories: the player she names is no longer what they were dealt, and their old card is out of the game.',
    ],
  },
  villageIdiot: {
    lead: 'Shifts every other player’s card one seat to the left or to the right.',
    does: [
      'The Village Idiot may move all the other players’ cards one place around the table, to the left or to the right. His own card stays where it is, and so does every shielded card. The center is not touched.',
    ],
    rules: [
      'Skipping, or running out of time, moves nothing.',
      'The shift goes around his own seat and around shielded cards: each moving card lands on the next seat that moves.',
      'He wakes right after the [[troublemaker]] and looks at no card.',
    ],
    tips: [
      'Say the direction out loud in the morning. After a shift, every claim about a dealt card is about the card in front of a neighbour, and the table cannot reason without it.',
      'A shift takes each wolf card away from the player who was dealt it. Those players know which neighbour holds it now.',
    ],
    against: ['A player dealt a Werewolf knows where that card went. Watch who they defend.'],
  },
  revealer: {
    lead: 'Turns one player’s card face up for the whole table, unless it is a Werewolf or the Tanner.',
    does: [
      'Near the end of the night the Revealer may look at one other player’s card. If it is not on the Werewolves’ team and not a Tanner, it stays face up and everyone sees it in the morning. Otherwise it is turned back down, and only the Revealer knows what it is.',
    ],
    rules: [
      'Vampire and Alien cards stay face up like village ones.',
      'Only a card that is still face down can be chosen, and not a shielded one.',
      'She acts after every card has moved, so the revealed card is what that player ends the night with.',
    ],
    tips: [
      'A face-up card is a fact nobody can argue with. Choose the player whose claim would be hardest to check otherwise.',
      'If the card went back down, you know a wolf or the Tanner for certain. Say so, and expect to be called a liar.',
    ],
    against: ['A Revealer who shows nothing either skipped or found something. Ask which.'],
  },
  curator: {
    lead: 'Gives one player a random artifact, face down. Some artifacts change that player’s role.',
    does: [
      'The Curator may place an artifact token on any player who has none, himself included. The artifact is drawn at random, and only its player looks at it, when the night ends.',
    ],
    rules: [
      'Six artifacts turn their player into a role for the vote, whatever card they hold: Claw of the Werewolf, Brand of the Villager, Cudgel of the Tanner, Bow of the Hunter, Sword of the Bodyguard, Cloak of the Prince.',
      'The Void of Nothingness does nothing. The Mask of Muting forbids its player to speak, and the Shroud of Shame makes them turn away from the table.',
      'The artifact stays with the player, not with the card, and a shield does not keep it away.',
      'The Curator does not see which artifact he gave.',
    ],
    tips: [
      'You do not know what you handed out. Ask the player, then decide whether you believe the answer.',
      'One artifact in nine makes a Werewolf and one in nine a Tanner. Giving it to a player you trust is a gamble.',
    ],
    against: [
      'A player with an artifact may have changed sides at the very end of the night, and only they know. See the [guide to artifacts](/guides/marks-and-artifacts/).',
    ],
  },
  dreamWolf: {
    lead: 'A Werewolf who sleeps through the night: the pack knows him, and he does not know the pack.',
    does: ['The Dream Wolf does not wake. When the Werewolves wake, they see who the Dream Wolf is.'],
    rules: [
      'He is a werewolf for the vote: if he dies, the Werewolves lose.',
      'The [[minion]] and the [[squire]] see him along with the other wolves.',
      'A Werewolf whose only partner is the Dream Wolf is not a lone wolf, and gets no look at the center.',
    ],
    tips: [
      'You know nothing and must lie anyway. Claim a quiet role and let the awake wolves find you in the conversation: they know who you are.',
    ],
    against: [
      'The awake wolves will protect a player who does not seem to know why. That player may be the Dream Wolf.',
    ],
  },
  bodyguard: {
    lead: 'The player the Bodyguard votes for cannot be killed.',
    does: ['The Bodyguard has no night action. In the vote, whoever the Bodyguard points at is protected.'],
    rules: [
      'If the protected player has the most votes, the player with the next most dies instead, provided they have at least two.',
      'The protection also stops the [[hunter]]’s shot. It does not save a lover: a Mark of Love kills through it.',
      'The power belongs to the card held when the night ends, and to the player with the [[curator]]’s Sword of the Bodyguard.',
    ],
    tips: [
      'Your vote is a shield, not a weapon. Point at the villager the table is about to vote out by mistake.',
      'Saying whom you will protect invites the wolves to redirect the vote. Keep it to yourself until the last seconds.',
    ],
    against: [
      'For a wolf, the Bodyguard is a way to survive: convince him that you are the innocent player about to be voted out.',
    ],
  },
} satisfies Partial<Record<RoleId, RoleGuide>>
